import { Repository } from "react3l-common";
import { kebabCase } from "lodash";
import { httpConfig } from "config/http";
import { BASE_API_URL } from "config/consts";
import { Observable } from "rxjs";
import { AxiosResponse } from "axios";
import nameof from "ts-nameof.macro";
import { API_MAJOR_PREFIX } from "config/api-consts";
import { Major, MajorFilter } from "models/Major";
import { Status, StatusFilter } from "models/Status";
import { Subject, SubjectFilter } from "models/Subject";
import { Organization, OrganizationFilter } from "models/Organization";

export type KeyType = string | number;

export class MajorRepository extends Repository {
  constructor() {
    super(httpConfig);
    this.baseURL = new URL(API_MAJOR_PREFIX, BASE_API_URL).href;
  }

  public count = (majorFilter?: MajorFilter): Observable<number> => {
    return this.http
      .post<number>(kebabCase(nameof(this.count)), majorFilter)
      .pipe(Repository.responseDataMapper<number>());
  };

  public list = (majorFilter?: MajorFilter): Observable<Major[]> => {
    return this.http
      .post<Major[]>(kebabCase(nameof(this.list)), majorFilter)
      .pipe(Repository.responseMapToList<Major>(Major));
  };

  public countSubject = (subjectFilter?: SubjectFilter): Observable<number> => {
    return this.http
      .post<number>(kebabCase(nameof(this.countSubject)), subjectFilter)
      .pipe(Repository.responseDataMapper<number>());
  };

  public listSubject = (subjectFilter?: SubjectFilter): Observable<Major[]> => {
    return this.http
      .post<Major[]>(kebabCase(nameof(this.listSubject)), subjectFilter)
      .pipe(Repository.responseMapToList<Major>(Major));
  };

  public get = (id: number | string): Observable<Major> => {
    return this.http
      .post<Major>(kebabCase(nameof(this.get)), { id })
      .pipe(Repository.responseMapToModel<Major>(Major));
  };

  public create = (major: Major): Observable<Major> => {
    return this.http
      .post<Major>(kebabCase(nameof(this.create)), major)
      .pipe(Repository.responseMapToModel<Major>(Major));
  };

  public update = (major: Major): Observable<Major> => {
    return this.http
      .post<Major>(kebabCase(nameof(this.update)), major)
      .pipe(Repository.responseMapToModel<Major>(Major));
  };

  public delete = (major: Major): Observable<Major> => {
    return this.http
      .post<Major>(kebabCase(nameof(this.delete)), major)
      .pipe(Repository.responseMapToModel<Major>(Major));
  };

  public bulkDelete = (idList: KeyType[]): Observable<void> => {
    return this.http
      .post(kebabCase(nameof(this.bulkDelete)), idList)
      .pipe(Repository.responseDataMapper());
  };

  public save = (major: Major): Observable<Major> => {
    return major.id ? this.update(major) : this.create(major);
  };

  public singleListStatus = (): Observable<Status[]> => {
    return this.http
      .post<Status[]>(
        kebabCase(nameof(this.singleListStatus)),
        new StatusFilter()
      )
      .pipe(Repository.responseMapToList<Status>(Status));
  };

  public filterListStatus = (): Observable<Status[]> => {
    return this.http
      .post<Status[]>(
        kebabCase(nameof(this.filterListStatus)),
        new StatusFilter()
      )
      .pipe(Repository.responseMapToList<Status>(Status));
  };

  public singleListSubject = (
    subjectFilter: SubjectFilter
  ): Observable<Subject[]> => {
    return this.http
      .post<Subject[]>(kebabCase(nameof(this.singleListSubject)), subjectFilter)
      .pipe(Repository.responseMapToList<Subject>(Subject));
  };
  public filterListSubject = (
    subjectFilter: SubjectFilter
  ): Observable<Subject[]> => {
    return this.http
      .post<Subject[]>(kebabCase(nameof(this.filterListSubject)), subjectFilter)
      .pipe(Repository.responseMapToList<Subject>(Subject));
  };

  public singleListOrganization = (
    organizationFilter: OrganizationFilter
  ): Observable<Organization[]> => {
    return this.http
      .post<Organization[]>(
        kebabCase(nameof(this.singleListOrganization)),
        organizationFilter
      )
      .pipe(Repository.responseMapToList<Organization>(Organization));
  };
  public filterListOrganization = (
    organizationFilter: OrganizationFilter
  ): Observable<Organization[]> => {
    return this.http
      .post<Organization[]>(
        kebabCase(nameof(this.filterListOrganization)),
        organizationFilter
      )
      .pipe(Repository.responseMapToList<Organization>(Organization));
  };

  public import = (
    file: File,
    name: string = nameof(file)
  ): Observable<void> => {
    const formData: FormData = new FormData();
    formData.append(name, file as Blob);
    return this.http
      .post<void>(kebabCase(nameof(this.import)), formData)
      .pipe(Repository.responseDataMapper<any>());
  };

  public importMapping = (
    file: File,
    name: string = nameof(file)
  ): Observable<void> => {
    const formData: FormData = new FormData();
    formData.append(name, file as Blob);
    return this.http
      .post<void>(kebabCase(nameof(this.importMapping)), formData)
      .pipe(Repository.responseDataMapper<any>());
  };

  public export = (filter: any): Observable<AxiosResponse<any>> => {
    return this.http.post("export", filter, {
      responseType: "arraybuffer",
    });
  };

  public exportTemplate = (): Observable<AxiosResponse<any>> => {
    return this.http.post(
      "export-template",
      {},
      {
        responseType: "arraybuffer",
      }
    );
  };

  public exportTemplateMapping = (): Observable<AxiosResponse<any>> => {
    return this.http.post(
      "export-template-mapping",
      {},
      {
        responseType: "arraybuffer",
      }
    );
  };
}

export const majorRepository = new MajorRepository();
